import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DashboardService } from '../services/dashboardService';
import { Dashboard } from '../types/Dashboard';
import { 
  AddDashboardRequest, 
  UpdateDashboardRequest,
  UserDashboardItem,
  SaveUserPreferencesRequest,
} from '../types/DashboardRequests';
import { useToast } from '@/shared/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { useAuth } from '@/features/Auth/state/AuthContext';
import { useSuperAdminOrg } from '@/shared/contexts/SuperAdminOrgContext';

export const useDashboards = (
  workspaceId?: string,
  scope?: 'platform' | 'organization' | 'product',
  tabId?: string
) => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { selectedOrganization } = useSuperAdminOrg();
  // PBI #78540: orgId for scope-aware writes — superadmin's currently-viewed
  // org takes precedence; falls back to session org for regular users.
  const effectiveOrgId = selectedOrganization?.id ?? user?.organizationId ?? undefined;
  const scopeContext = {
    scope,
    workspaceId,
    orgId:
      scope === 'organization' || scope === 'product' ? effectiveOrgId : undefined,
  };

  // tabId is part of the queryKey so each tab keeps its own cache. Switching
  // away and back is instant: React Query returns the previous tab's cached
  // result while it revalidates in the background. Without this, the cache
  // is shared and our optimistic logic in useDashboardTabs has to filter it
  // — but the filter can't reconstruct data we no longer hold (e.g. when
  // returning from an empty tab to one with dashboards we already loaded).
  // PBI #78540: orgId entra na queryKey + no request. Sem isso, superadmin
  // trocando de org via SuperAdminOrgContext continua vendo o cache do org
  // anterior (a queryKey não muda → React Query não refetch). Para org/product
  // scope, o backend usa o orgId para filtrar dashboards_base.
  const queryOrgId = scopeContext.orgId;
  const {
    data: dashboards = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery<Dashboard[]>({
    queryKey: ['dashboards', queryOrgId, workspaceId, scope, tabId],
    queryFn: async () => {
      return await DashboardService.getUserDashboards({ workspaceId, scope, tabId, orgId: queryOrgId });
    },
    staleTime: 5 * 60 * 1000,
  });

  const addDashboardMutation = useMutation({
    mutationFn: (request: AddDashboardRequest) => DashboardService.addDashboard(request, scopeContext),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
      queryClient.invalidateQueries({ queryKey: ['available-dashboards'] });
      // PBI #78540: tabs e layouts vivem em `dashboard-preferences` cache. Sem
      // invalidar aqui, ações posteriores (rename tab, save layout) operam
      // sobre tabs stale e sobrescrevem os items recém-adicionados.
      queryClient.invalidateQueries({ queryKey: ['dashboard-preferences'] });
      toast({
        title: t('dashboards.addSuccess'),
        variant: 'default',
      });
    },
    onError: () => {
      toast({
        title: t('dashboards.addError'),
        variant: 'destructive',
      });
    },
  });

  // Novo sistema: salvar preferências completas com optimistic update.
  //
  // Importante: useDashboards keya a query por (workspaceId, scope, tabId).
  // Usamos `setQueriesData`/`cancelQueries` no prefixo broad `['dashboards']`
  // para que o optimistic update atinja a chave correta independentemente do
  // tabId atual, e também todas as outras tabs do mesmo workspace (cada
  // dashboard tem o mesmo ID em qualquer tab onde apareça). `onSettled` ainda
  // invalida o prefixo broad para garantir que o servidor confirme o estado.
  const savePreferencesMutation = useMutation({
    mutationFn: (request: SaveUserPreferencesRequest) => {
      // PBI #78540: pass the current scope context so the backend writes into
      // user_settings.byScope[scope][...] rather than the legacy flat root.
      // Without this, edits never produce a per-user override and the user
      // stays married to whatever default is cascading in.
      return DashboardService.saveUserPreferences(request, { scope, workspaceId });
    },
    onMutate: async (request) => {
      await queryClient.cancelQueries({ queryKey: ['dashboards'] });

      const previousDashboardsByKey = queryClient.getQueriesData<Dashboard[]>({
        queryKey: ['dashboards'],
      });

      const itemsMap = new Map((request.items ?? []).map(item => [item.dashboardBaseId, item]));

      queryClient.setQueriesData<Dashboard[]>({ queryKey: ['dashboards'] }, (old) => {
        if (!old) return old;
        return old.map(dashboard => {
          const item = itemsMap.get(dashboard.id);
          if (item) {
            return {
              ...dashboard,
              position: item.position,
              size: item.size,
              filters: item.filters,
              customName: item.customName,
              is_active: item.visible ?? true,
            };
          }
          return dashboard;
        }).sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      });

      return { previousDashboardsByKey };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousDashboardsByKey) {
        context.previousDashboardsByKey.forEach(([key, data]) => {
          queryClient.setQueryData(key, data);
        });
      }
    },
    onSettled: () => {
      // Safety net: ensure the server-side state is fetched. The optimistic
      // update already painted the UI; this just confirms or corrects it.
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
      // PBI #78540: any items/layouts save also touches tabs[active] no backend
      // via syncActiveTabFromRoot. Sem invalidar prefs, useDashboardTabs vê
      // tabs stale e rename/delete tab sobrescreve mudanças recentes.
      queryClient.invalidateQueries({ queryKey: ['dashboard-preferences'] });
    },
  });

  // Converter dashboards para UserDashboardItems
  const convertToUserDashboardItems = useCallback((dashboards: Dashboard[]): UserDashboardItem[] => {
    return dashboards.map((dashboard, index) => ({
      id: dashboard.user_dashboard_id || dashboard.id,
      dashboardBaseId: dashboard.id,
      position: dashboard.position ?? index,
      size: dashboard.size,
      filters: dashboard.filters,
      visible: dashboard.is_active ?? true,
      customName: dashboard.customName,
    }));
  }, []);

  // Salvar preferências completas
  const saveAllPreferences = useCallback((updatedDashboards: Dashboard[]) => {
    const items = convertToUserDashboardItems(updatedDashboards);
    const request = {
      items,
      layout: 'grid' as const,
      gridColumns: 2,
    };
    savePreferencesMutation.mutate(request);
  }, [convertToUserDashboardItems, savePreferencesMutation]);

  // Atualizar um dashboard específico mas enviar lista completa
  const updateDashboard = useCallback((
    { dashboardId, request }: { dashboardId: string; request: UpdateDashboardRequest }
  ) => {
    const updatedDashboards = dashboards.map(dashboard =>
      dashboard.id === dashboardId
        ? { ...dashboard, ...request }
        : dashboard
    );

    saveAllPreferences(updatedDashboards);
  }, [dashboards, saveAllPreferences]);

  const removeDashboardMutation = useMutation({
    mutationFn: (dashboardId: string) => DashboardService.removeDashboard(dashboardId, scopeContext),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
      queryClient.invalidateQueries({ queryKey: ['available-dashboards'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-preferences'] });
      toast({
        title: t('dashboards.removeSuccess'),
        variant: 'default',
      });
    },
    onError: () => {
      toast({
        title: t('dashboards.removeError'),
        variant: 'destructive',
      });
    },
  });

  // Reordenar dashboards
  const reorderDashboards = useCallback((reorderedDashboards: Dashboard[]) => {
    const updatedDashboards = reorderedDashboards.map((dashboard, index) => ({
      ...dashboard,
      position: index,
    }));
    saveAllPreferences(updatedDashboards);
  }, [saveAllPreferences]);

  return {
    dashboards,
    isLoading,
    isFetching,
    error,
    refetch,
    addDashboard: addDashboardMutation.mutate,
    updateDashboard,
    removeDashboard: removeDashboardMutation.mutate,
    reorderDashboards,
    saveAllPreferences,
    isAddingDashboard: addDashboardMutation.isPending,
    isUpdatingDashboard: savePreferencesMutation.isPending,
    isRemovingDashboard: removeDashboardMutation.isPending,
  };
};
